import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PdfThumbnail } from "@/components/PdfThumbnail";
import { Upload, Trash2, Copy, ExternalLink, Play, FolderOpen, Check, Loader2, Music, FileText } from "lucide-react";

const absoluteUrl = (url) => (url?.startsWith("http") ? url : `${window.location.origin}${url}`);

function MediaTile({ item }) {
  if (item.type === "video") {
    return (
      <>
        <video src={item.url} className="w-full h-full object-cover pointer-events-none" muted preload="metadata" />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="w-8 h-8 rounded-full bg-white/80 flex items-center justify-center">
            <Play className="w-3.5 h-3.5 text-foreground ml-0.5" />
          </div>
        </div>
      </>
    );
  }
  if (item.type === "pdf") return <PdfThumbnail url={item.url} filename={item.filename} />;
  if (item.type === "audio" || item.type === "document") {
    const Icon = item.type === "audio" ? Music : FileText;
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-muted gap-1 pb-5 pointer-events-none">
        <Icon className="w-8 h-8 text-primary" />
        <span className="text-[10px] font-bold uppercase text-muted-foreground mt-1">{(item.filename || "").split(".").pop()}</span>
      </div>
    );
  }
  return <img src={item.url} alt={item.filename || ""} className="w-full h-full object-cover pointer-events-none" />;
}

export function MediaLibrary({ selectable = false, onSelect }) {
  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/cms/media`, { headers: authHeaders });
      setMedia(r.data.media || []);
    } catch (err) {
      console.error("media load failed", err?.response?.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let done = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await axios.post(`${API}/cms/media`, fd, { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
        done++;
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload "${file.name}"`);
      }
    }
    if (done > 0) toast.success(`${done} file(s) added to library`);
    setUploading(false);
    e.target.value = "";
    load();
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.filename || "this asset"}" from the library?`)) return;
    try {
      await axios.delete(`${API}/cms/media/${item.media_id}`, { headers: authHeaders });
      setMedia((prev) => prev.filter((m) => m.media_id !== item.media_id));
      toast.success("Media deleted");
    } catch {
      toast.error("Failed to delete media");
    }
  };

  const copyUrl = async (item) => {
    try {
      await navigator.clipboard.writeText(absoluteUrl(item.url));
      toast.success("URL copied to clipboard");
    } catch {
      toast.error("Couldn't copy URL");
    }
  };

  return (
    <div className="space-y-3" data-testid="media-library">
      <div className="border-2 border-dashed border-border rounded-xl p-5 text-center">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime,audio/*,application/pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ods,.odp,.zip"
          multiple
          onChange={handleUpload}
          className="hidden"
          id="media-library-upload"
          data-testid="media-library-upload-input"
        />
        <label htmlFor="media-library-upload" className="cursor-pointer">
          {uploading ? (
            <Loader2 className="w-7 h-7 text-muted-foreground mx-auto mb-2 animate-spin" />
          ) : (
            <Upload className="w-7 h-7 text-muted-foreground mx-auto mb-2" />
          )}
          <p className="text-sm text-muted-foreground">{uploading ? "Uploading..." : "Click to upload images, videos, audio, PDFs or documents"}</p>
          <p className="text-[11px] text-muted-foreground mt-1">Reuse these assets across the website, events, banners and lessons. Images max 10MB · PDFs, documents (Word/PPT/Excel/CSV/TXT/ZIP) &amp; audio max 25MB · Videos max 50MB.</p>
        </label>
      </div>

      {media.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground" data-testid="media-library-empty">
          <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No media yet. Upload assets to reuse them anywhere.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="media-library-grid">
          {media.map((item) => (
            <div
              key={item.media_id}
              className={`relative rounded-xl overflow-hidden border bg-muted/20 aspect-video group transition-all ${selectable ? "cursor-pointer hover:ring-2 hover:ring-primary" : "border-border"}`}
              onClick={selectable ? () => onSelect?.(item) : undefined}
              data-testid={`media-item-${item.media_id}`}
            >
              <MediaTile item={item} />

              {selectable && (
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 flex items-center justify-center transition-colors">
                  <div className="opacity-0 group-hover:opacity-100 w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center transition-opacity">
                    <Check className="w-4 h-4" />
                  </div>
                </div>
              )}

              {!selectable && (
                <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button className="w-7 h-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow" onClick={(e) => { e.stopPropagation(); copyUrl(item); }} title="Copy URL" data-testid={`media-copy-${item.media_id}`}>
                    <Copy className="w-3.5 h-3.5 text-foreground" />
                  </button>
                  <a className="w-7 h-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow" href={item.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="Open in new tab" data-testid={`media-open-${item.media_id}`}>
                    <ExternalLink className="w-3.5 h-3.5 text-foreground" />
                  </a>
                  <button className="w-7 h-7 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center shadow" onClick={(e) => { e.stopPropagation(); remove(item); }} title="Delete" data-testid={`media-delete-${item.media_id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-white" />
                  </button>
                </div>
              )}

              <div className="absolute bottom-1.5 left-1.5">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/60 text-white uppercase tracking-wide">{item.type}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function MediaPicker({ open, onOpenChange, onSelect }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="media-picker-dialog">
        <DialogHeader>
          <DialogTitle>Media Library</DialogTitle>
          <DialogDescription>Click an asset to use it, or upload a new one.</DialogDescription>
        </DialogHeader>
        <MediaLibrary
          selectable
          onSelect={(item) => { onSelect?.(item); onOpenChange?.(false); }}
        />
      </DialogContent>
    </Dialog>
  );
}
